import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { apiService } from './apiService';
import type {
  Company as GeneratedCompany,
  CompanyCreateRequest as GeneratedCompanyCreateRequest,
  CompanyUpdateRequest as GeneratedCompanyUpdateRequest,
  CompaniesPaginatedResponse as GeneratedCompaniesPaginatedResponse,
} from '../../generated-src/client/companies/api';

export type CompanyModuleCode = 'SEARCH' | 'DIALOG' | 'INTERVIEW' | string;

export interface CompanyStats {
  searchKopecks: number;
  searchUnits: number;
  dialogKopecks: number;
  dialogUnits: number;
  interviewKopecks: number;
  interviewUnits: number;
  totalSpentKopecks?: number | null;
}

export interface Company extends Omit<GeneratedCompany, 'status' | 'stats' | 'accountsCount' | 'balanceKopecks'> {
  status: GeneratedCompany['status'] | 'PILOT';
  ownerEmail?: string | null;
  accountsCount?: number | null;
  maxAccounts?: number | null;
  balanceKopecks?: number | null;
  stats?: CompanyStats | null;
  pilotStartDate?: string | null;
  subscriptionStartDate?: string | null;
  relevanterEnabled?: boolean | null;
  atsEnabled?: boolean | null;
}

export interface CompaniesPaginatedResponse extends Omit<GeneratedCompaniesPaginatedResponse, 'content'> {
  content: Company[];
}

export interface CompanyCreateRequest extends Omit<GeneratedCompanyCreateRequest, 'ownerEmail'> {
  ownerEmail?: string;
  pilotStartDate?: string;
  subscriptionStartDate?: string;
  initialBalanceKopecks?: number;
  maxAccounts?: number;
  searchCreditsPriceKopecks?: number;
  dialogCreditsPriceKopecks?: number;
  interviewCreditsPriceKopecks?: number;
  relevanterEnabled?: boolean;
  atsEnabled?: boolean;
}

export interface CompanyUpdateRequest extends Omit<GeneratedCompanyUpdateRequest, 'status'> {
  ownerEmail?: string;
  status?: Company['status'];
  subscriptionStartDate?: string;
  maxAccounts?: number;
  searchCreditsPriceKopecks?: number;
  dialogCreditsPriceKopecks?: number;
  interviewCreditsPriceKopecks?: number;
  relevanterEnabled?: boolean;
  atsEnabled?: boolean;
}

export interface CompanyTopUpRequest {
  amountKopecks: number;
  reason?: string;
}

export interface CompanyPricingRequest {
  searchCreditsPriceKopecks: number;
  dialogCreditsPriceKopecks: number;
  interviewCreditsPriceKopecks: number;
}

export interface CompanyPricingResponse extends CompanyPricingRequest {}

export interface CompanyBalanceStats {
  balanceKopecks: number;
  servicesDisabled?: boolean | null;
  prognosisDays?: number | null;
}

export interface CompanyModuleAnalyticsItem {
  module: CompanyModuleCode;
  moduleName: string;
  tariffKopecks: number;
  costPerUnitKopecks: number;
  units: number;
  revenueKopecks: number;
  costKopecks: number;
}

export interface CompanyModuleAnalytics {
  companyName?: string | null;
  balanceKopecks: number;
  revenueKopecks: number;
  costKopecks: number;
  modules: CompanyModuleAnalyticsItem[];
  companyDescription?: string | null;
}

/** Строка статистики сотрудника компании (таблица «Показатели» в карточке компании). */
export interface CompanyRecruiterStat {
  userId: number;
  /** ФИО */
  name: string;
  email?: string | null;
  /** Роль для отображения (напр. «Суперадмин») */
  role?: string | null;
  /** Онлайн-индикатор */
  online?: boolean;
  /** Активных вакансий */
  positions: number;
  /** Диалогов */
  dialogs: number;
  /** Ответы, % */
  responseRate: number;
  /** Интервью, % */
  interviewRate: number;
  /** Одобрено, % */
  approvedRate: number;
  /** Среднее время, минут */
  avgTimeMinutes: number;
}

export interface CompanyRecruitersPage {
  content: CompanyRecruiterStat[];
  totalPages: number;
  totalElements: number;
}

/** Баланс компании для карточки суперадмина (агрегат по лимитам, всё в копейках). */
export interface CompanyBalanceWidget {
  /** Текущий рублёвый баланс компании (крупное число) */
  balanceKopecks: number;
  /** DEPRECATED (штуки→рубли): штучный пул, больше не показываем. */
  totalAllocatedKopecks: number;
  /** Можно распределить (баланс − выдано лимитами − траты), копейки. Большое число. */
  availableToDistributeKopecks: number;
  /** Всего пополнено за период (баланс + траты), копейки. Серое «/ …». */
  totalToppedUpKopecks: number;
  /** Выдано лимитами сотрудникам */
  reservedByLimitsKopecks: number;
  /** Траты без лимитов (Общий пул) */
  spentByUnlimitedKopecks: number;
}

/**
 * Цвет текста прогноза по уровню баланса (TASKNEIROKLYUCH-855):
 * красный — услуги отключены, оранжевый — критично (<20 дн.), янтарный — мало (<30 дн.),
 * серый — норма.
 */
export function getPrognosisColor(stats: CompanyBalanceStats | null | undefined): string {
  if (!stats) return '#525866';
  if (stats.servicesDisabled) return '#ef4444'; // red-500 — услуги отключены
  const days = stats.prognosisDays;
  if (days != null && days < 20) return '#f97316'; // orange-500 — критично
  if (days != null && days < 30) return '#f59e0b'; // amber-500 — предупреждение
  return '#525866'; // серый — норма
}

export interface ListCompaniesParams {
  search?: string;
  /** Один статус или несколько через запятую (REL-195 мультифильтр): "ACTIVE,PILOT". */
  status?: string;
  page?: number;
  size?: number;
  sort?: string;
  dateFrom?: string; // ISO date YYYY-MM-DD
  dateTo?: string;
  includeStats?: boolean;
}

/** Форматирует копейки в рубли с разделителями */
export function kopToRub(kopecks: number): string {
  return Math.round(kopecks / 100).toLocaleString('ru-RU');
}

/** Форматирует копейки в рубли с копейками */
export function kopToRubFull(kopecks: number): string {
  return (kopecks / 100).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Формат суммы как в макете: целые рубли — без копеек ("89 000"), дробные — с копейками
 * ("677 500,96"). Знак валюты добавляется в разметке отдельно.
 */
export function kopToRubSmart(kopecks: number): string {
  return kopecks % 100 === 0
    ? Math.round(kopecks / 100).toLocaleString('ru-RU')
    : kopToRubFull(kopecks);
}

/** Компактный формат числа: 48000 → "48К", 1500000 → "1,5М" */
export function formatUnitsCompact(units: number): string {
  if (units >= 1_000_000) return `${(units / 1_000_000).toFixed(1).replace('.0', '')}М`;
  if (units >= 1_000) return `${Math.round(units / 1_000)}К`;
  return String(units);
}

/** Цена за единицу из kopecks: 100 коп → "1₽", 50 коп → "0.5₽" */
export function formatPricePerUnit(kopecks: number): string {
  const rub = kopecks / 100;
  return `${rub % 1 === 0 ? rub.toFixed(0) : rub.toFixed(1)}₽`;
}

/** Форматирует строку `48К шт. (1₽)` для ячейки статистики */
export function formatStatsUnits(units: number, priceKopecks: number): string {
  return `${formatUnitsCompact(units)} шт. (${formatPricePerUnit(priceKopecks)})`;
}

/** Форматирует дельту с + / - знаком */
export function formatDelta(kopecks: number): string {
  const sign = kopecks >= 0 ? '+' : '';
  return `${sign}${kopToRubFull(kopecks)} ₽`;
}

/** Возвращает % дельты относительно предыдущего периода */
export function formatDeltaPct(current: number, delta: number): string {
  if (current === 0 || delta === 0) return '(0%)';
  const prev = current - delta;
  if (prev === 0) return '(+∞%)';
  const pct = Math.round((delta / Math.abs(prev)) * 100);
  return `(${pct > 0 ? '+' : ''}${pct}%)`;
}

/** Метки статусов */
export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активная',
  PILOT: 'Пилотная',
  SUSPENDED: 'На паузе',
  INACTIVE: 'Заблокированная',
};

/** Цвета точек для статусов (hex строго по макету Figma) */
export const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#38C793',    // зелёный — активна
  PILOT: '#6E3FF3',     // фиолетовый — на пилоте (значок-подарок, REL-195)
  SUSPENDED: '#F27B2C', // оранжевый — на паузе
  INACTIVE: '#DF1C41',  // красный — заблокирована
};

class CompaniesManagementService {
  async getCompanies(params: ListCompaniesParams = {}): Promise<CompaniesPaginatedResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.size !== undefined) query.set('size', String(params.size));
    if (params.sort) query.set('sort', params.sort);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.includeStats !== undefined) query.set('includeStats', String(params.includeStats));

    const qs = query.toString();
    const res = await apiService.get(`/companies${qs ? `?${qs}` : ''}`);
    return res.data;
  }

  async getCompany(id: number): Promise<Company> {
    const res = await apiService.get(`/companies/${id}`);
    return res.data;
  }

  async createCompany(data: CompanyCreateRequest): Promise<Company> {
    const res = await apiService.post('/companies', data);
    return res.data;
  }

  async updateCompany(id: number, data: CompanyUpdateRequest): Promise<Company> {
    const res = await apiService.put(`/companies/${id}`, data);
    return res.data;
  }

  async deleteCompany(id: number): Promise<void> {
    await apiService.delete(`/companies/${id}`);
  }

  async updateStatus(
    id: number,
    status: 'ACTIVE' | 'PILOT' | 'SUSPENDED' | 'INACTIVE',
  ): Promise<Company> {
    const token = useAuthStore.getState().token;
    const apiBase = (process.env.REACT_APP_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
    const res = await axios.patch(
      `${apiBase}/companies/${id}/status`,
      { status },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return res.data;
  }

  async topUpBalance(id: number, request: CompanyTopUpRequest): Promise<Company> {
    const res = await apiService.post(`/companies/${id}/top-up`, request);
    return res.data;
  }

  async getPricing(id: number): Promise<CompanyPricingResponse> {
    const res = await apiService.get(`/companies/${id}/pricing`);
    return res.data;
  }

  async setPricing(id: number, request: CompanyPricingRequest): Promise<CompanyPricingResponse> {
    const res = await apiService.put(`/companies/${id}/pricing`, request);
    return res.data;
  }

  /**
   * Статистика баланса компании с прогнозом дней (TASKNEIROKLYUCH-855). Используется для колонки
   * БАЛАНС/ПРОГНОЗ в панели управления компаниями.
   */
  async getBalanceStats(
    id: number,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<CompanyBalanceStats> {
    const res = await apiService.get(`/companies/${id}/balance-stats`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  }

  /**
   * Баланс компании для карточки (вид суперадмина): текущий баланс + распределение по лимитам.
   */
  async getCompanyBalance(id: number): Promise<CompanyBalanceWidget> {
    const res = await apiService.get(`/companies/${id}/credits/balance`);
    return res.data;
  }

  /**
   * Статистика сотрудников компании для карточки (вкладка «Показатели»): вакансии, диалоги,
   * проценты ответов/интервью/одобрения, среднее время. Пагинация + поиск по имени/email.
   */
  async getCompanyRecruiters(
    id: number,
    params: { search?: string; page?: number; size?: number } = {},
  ): Promise<CompanyRecruitersPage> {
    const res = await apiService.get(`/companies/${id}/stats/recruiters`, {
      params: {
        search: params.search || undefined,
        page: params.page ?? 0,
        size: params.size ?? 10,
      },
    });
    return res.data;
  }

  /**
   * Аналитика по ИИ-модулям компании (REL-195, окно «Аналитика по модулям»): тариф/себестоимость/
   * количество/выручка по модулям + выручено/себестоимость/прибыль. Маржа считается на фронте.
   */
  async getModuleAnalytics(
    id: number,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<CompanyModuleAnalytics> {
    const res = await apiService.get(`/companies/${id}/module-analytics`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  }

  /** Суммарный баланс всех компаний (для виджета) */
  getTotalBalanceKopecks(companies: Company[]): number {
    return companies.reduce((sum, c) => sum + (c.balanceKopecks ?? 0), 0);
  }

  /** Суммарный оборот ИИ по всем компаниям (для виджета) */
  getTotalAiTurnoverKopecks(companies: Company[]): number {
    return companies.reduce((sum, c) => {
      const s = c.stats;
      if (!s) return sum;
      return sum + (s.searchKopecks ?? 0) + (s.dialogKopecks ?? 0) + (s.interviewKopecks ?? 0);
    }, 0);
  }
}

export const companiesManagementService = new CompaniesManagementService();
